import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, Boxes, Hammer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Admin · Inventory — read-only stock across every vendor, lowest first.
 *
 * Answers the one question no other screen could before launch: who is about to run dry.
 * Each vendor's own low-stock threshold decides what counts as low, so "low" means what
 * that vendor says it means. Editing stays with the vendor; this is for seeing.
 */
export default function AdminInventoryTab() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lowOnly, setLowOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inventory?low_only=${lowOnly}&per_page=300`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load inventory');
    } finally { setLoading(false); }
  }, [lowOnly]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="admin-inventory-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Boxes className="w-4 h-4" /> Inventory
          </h3>
          <p className="text-sm text-gray-400">
            Every sellable unit across all storefronts, lowest stock first. Read-only — vendors
            keep their own counts.
          </p>
        </div>
        <Button
          variant={lowOnly ? 'default' : 'outline'}
          onClick={() => setLowOnly(v => !v)}
          data-testid="admin-inventory-low-filter"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Low stock ({data?.low_count ?? 0})
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm" data-testid="admin-inventory-table">
          <thead className="bg-gray-800/60 text-gray-300">
            <tr>
              <th className="text-left px-3 py-2">Store</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Stock</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`${r.product_id}-${r.variant_name || 'base'}-${i}`}
                className="border-t border-gray-800"
                data-testid={`admin-inventory-row-${i}`}
              >
                <td className="px-3 py-2 text-gray-400">{r.store_name}</td>
                <td className="px-3 py-2">{r.title}</td>
                <td className="px-3 py-2 text-gray-400">{r.variant_name || '—'}</td>
                <td className="px-3 py-2 text-right">
                  {r.made_to_order ? (
                    <span className="inline-flex items-center gap-1 text-amber-400">
                      <Hammer className="w-3 h-3" /> Made to order
                    </span>
                  ) : !r.tracked ? (
                    <span className="text-gray-500">Not counted</span>
                  ) : (
                    <span className={
                      r.stock === 0 ? 'text-red-400 font-semibold'
                        : r.low ? 'text-amber-400 font-semibold' : 'text-gray-200'
                    }>
                      {r.stock}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                {lowOnly ? 'Nothing is running low.' : 'No physical products yet.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">
        Showing {rows.length} of {data?.total ?? 0} rows.
      </p>
    </div>
  );
}
